import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

export default function ContactPage() {
    const { t } = useTranslation();
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/public/contact", form);
            trackEvent("generate_lead", { lead_source: "kontakt" });
            toast.success(t("contact.success"));
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || t("contact.error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <PublicLayout>
            <Seo
                title="Kontakt: Wir helfen bei deiner Bewerbung | Bewerbung24.ai"
                description="Fragen zu KI-Bewerbung, Preisen oder Partnerschaften? Schreib uns über das Kontaktformular oder direkt an info@bewerbung24.ai — wir antworten schnell."
                path="/kontakt"
            />
            <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-12 lg:pb-20 grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20 items-start">
                <div>
                    <div className="h-1.5 w-16 rounded-full mb-7" style={{ background: "linear-gradient(35deg, #E7343F 0%, #F9B000 100%)" }} />
                    <span className="eyebrow-warm">{t("contact.badge")}</span>
                    <h1 className="font-heading mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-bold tracking-tighter text-warm-strong leading-[0.95]">
                        {t("contact.h1Part1")}<br />
                        <span className="gradient-warm">{t("contact.h1Highlight")}</span>
                    </h1>
                    <p className="mt-7 text-lg text-warm leading-relaxed">{t("contact.subtitle")}</p>
                    <div className="mt-10 space-y-3 text-sm">
                        <p className="text-warm">{t("contact.altEmail")}</p>
                        <a href="mailto:info@bewerbung24.ai" data-testid="contact-mailto" className="inline-flex items-center gap-2 font-heading text-2xl font-bold text-warm-strong hover:text-warm-red transition-colors">
                            <Mail size={20} /> info@bewerbung24.ai
                        </a>
                    </div>
                </div>

                <form className="p-7 lg:p-9 rounded-3xl bg-white shadow-xl space-y-4" style={{ borderTop: "5px solid #E7343F" }} onSubmit={submit} data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="contact-name" className="text-xs font-bold uppercase tracking-wider text-warm-red">{t("common.name")}</label>
                            <input id="contact-name" required data-testid="contact-name-input" value={form.name} onChange={upd("name")}
                                className="w-full mt-1.5 px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 focus:border-warm-red outline-none text-sm" />
                        </div>
                        <div>
                            <label htmlFor="contact-email" className="text-xs font-bold uppercase tracking-wider text-warm-red">{t("common.email")}</label>
                            <input id="contact-email" type="email" required data-testid="contact-email-input" value={form.email} onChange={upd("email")}
                                className="w-full mt-1.5 px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 focus:border-warm-red outline-none text-sm" />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="contact-subject" className="text-xs font-bold uppercase tracking-wider text-warm-red">{t("contact.subject")}</label>
                        <input id="contact-subject" required data-testid="contact-subject-input" value={form.subject} onChange={upd("subject")}
                            className="w-full mt-1.5 px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 focus:border-warm-red outline-none text-sm" />
                    </div>
                    <div>
                        <label htmlFor="contact-message" className="text-xs font-bold uppercase tracking-wider text-warm-red">{t("contact.message")}</label>
                        <textarea id="contact-message" required rows={6} data-testid="contact-message-input" value={form.message} onChange={upd("message")}
                            className="w-full mt-1.5 px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200 focus:border-warm-red outline-none text-sm" />
                    </div>
                    <button type="submit" disabled={busy} data-testid="contact-submit-button"
                        className="w-full py-3.5 rounded-full font-semibold btn-primary-grad disabled:opacity-60 inline-flex items-center justify-center gap-2">
                        <Send size={16} /> {busy ? t("contact.sending") : t("contact.send")}
                    </button>
                </form>
            </section>
        </PublicLayout>
    );
}
